package com.shadowings.gero.presentation.screen

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.foundation.Image
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.pager.VerticalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavController
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.Message
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.reminder.NotificationPermissionRequestEffect
import com.shadowings.gero.domain.reminder.openExactAlarmPermissionSettings
import com.shadowings.gero.domain.reminder.rememberExactAlarmPermissionGranted
import com.shadowings.gero.domain.reminder.rememberNotificationPermissionGranted
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.component.avatars.AlynAvatar
import com.shadowings.gero.presentation.component.avatars.NiloAvatar
import com.shadowings.gero.presentation.model.IntroEffect
import com.shadowings.gero.presentation.model.IntroUiState
import com.shadowings.gero.presentation.navigation.Destinations
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.viewmodel.IntroViewModel
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronDown
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.inizia_btn
import gero.composeapp.generated.resources.intro_btn_request_notifications
import gero.composeapp.generated.resources.intro_btn_request_timer
import gero.composeapp.generated.resources.intro_notifications_granted
import gero.composeapp.generated.resources.intro_timer_granted
import gero.composeapp.generated.resources.intro_title
import gero.composeapp.generated.resources.scroll_down_desc
import gero.composeapp.generated.resources.scroll_down_label
import gero.composeapp.generated.resources.triangle
import gero.composeapp.generated.resources.triangle_desc
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun IntroScreenPreview() {
    val messages = listOf(
        Message(
            Characters.Alyn,
            com.shadowings.gero.domain.model.Faces.Surprised,
            "Ciao! Benvenuto in Gero!"
        ),
        Message(
            Characters.Nilo,
            com.shadowings.gero.domain.model.Faces.Idle,
            "Miao! Cominciamo lo studio?"
        )
    )
    AppTheme {
        Surface {
            IntroScreenContent(
                uiState = IntroUiState(
                    messages = messages,
                    userData = UserData()
                ),
                onCompleteTutorial = {}
            )
        }
    }
}

@Composable
fun IntroScreen(
    navController: NavController,
    viewModel: IntroViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    LaunchedEffect(Unit) {
        viewModel.effects.collect { effect ->
            when (effect) {
                IntroEffect.NavigateToMain -> {
                    navController.navigate(route = Destinations.MAIN_LANDING) {
                        popUpTo(0) { inclusive = true }
                    }
                }

                else -> { /* No-op, scroll handled by pager */
                }
            }
        }
    }

    IntroScreenContent(
        uiState = uiState,
        onCompleteTutorial = { viewModel.completeTutorial() }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun IntroScreenContent(
    uiState: IntroUiState,
    onCompleteTutorial: () -> Unit,
) {
    val userData = uiState.userData
    val messages = uiState.messages

    if (messages.isEmpty()) {
        Scaffold(modifier = Modifier.fillMaxSize()) { padding ->
            Box(Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        }
        return
    }

    val pageCount = messages.size
    val pagerState = rememberPagerState(0) { pageCount }
    val coroutineScope = rememberCoroutineScope()
    val notificationsPage = remember(pageCount) { (pageCount - 3).coerceAtLeast(0) }
    val timerPage = remember(pageCount) { (pageCount - 2).coerceAtLeast(0) }
    val exactAlarmGranted = rememberExactAlarmPermissionGranted()
    val notificationsGranted = rememberNotificationPermissionGranted()
    var shouldRequestPermission by rememberSaveable { mutableStateOf(false) }

    NotificationPermissionRequestEffect(
        triggerRequest = shouldRequestPermission,
        onRequestHandled = {
            shouldRequestPermission = false
        },
    )

    AppTheme {
        Surface {
            Scaffold(
                modifier = Modifier.fillMaxSize()
            ) { padding ->
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(padding)
                        .semantics { testTag = "intro_screen" },
                    verticalArrangement = Arrangement.Center,
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Title card of the tutorial
                    ElevatedCard(
                        modifier = Modifier
                            .padding(horizontal = 16.dp, vertical = 8.dp)
                            .border(
                                width = 2.dp,
                                color = MaterialTheme.colorScheme.onSecondaryContainer,
                                shape = CardDefaults.shape
                            )
                            .semantics { testTag = "intro_title_card" },
                        colors = CardDefaults.cardColors(
                            containerColor = MaterialTheme.colorScheme.secondaryContainer
                        )
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(12.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center
                        ) {
                            Text(
                                text = stringResource(Res.string.intro_title),
                                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                                color = MaterialTheme.colorScheme.onSecondaryContainer,
                                textAlign = TextAlign.Center
                            )
                        }
                    }

                    // Vertical Pager for each tutorial step
                    VerticalPager(
                        state = pagerState,
                        modifier = Modifier
                            .weight(1f)
                            .semantics { testTag = "intro_pager" }
                    ) { page ->
                        IntroCardComposable(
                            message = messages[page],
                            userData = userData,
                            page = page,
                            notificationsPage = notificationsPage,
                            timerPage = timerPage,
                            notificationsGranted = notificationsGranted,
                            exactAlarmGranted = exactAlarmGranted,
                            onRequestNotifications = {
                                shouldRequestPermission = true
                            },
                            onRequestExactAlarm = {
                                openExactAlarmPermissionSettings()
                            }
                        )
                    }

                    val endReached = remember { mutableStateOf(false) }

                    LaunchedEffect(pagerState.currentPage) {
                        if (!endReached.value) {
                            endReached.value = pagerState.currentPage == pageCount - 1
                        }
                    }

                    AnimatedVisibility(
                        visible = endReached.value,
                        enter = fadeIn() + scaleIn(),
                        exit = fadeOut() + scaleOut()
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(16.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            GeroButton(
                                title = stringResource(Res.string.inizia_btn),
                                testTagId = "intro_btn_next",
                                modifier = Modifier.width(280.dp)
                            ) {
                                onCompleteTutorial()
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun IntroCardComposable(
    message: Message,
    userData: UserData,
    page: Int,
    notificationsPage: Int = -1,
    timerPage: Int = -1,
    notificationsGranted: Boolean = false,
    exactAlarmGranted: Boolean = false,
    onRequestNotifications: () -> Unit = {},
    onRequestExactAlarm: () -> Unit = {}
) {
    ElevatedCard(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = 32.dp, vertical = 24.dp)
            .semantics { testTag = "intro_page_$page" },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Avatar Background Container
            if (message.characters == Characters.Nilo) {
                NiloAvatar(
                    face = message.faces,
                    userData = userData
                )
            } else {
                AlynAvatar(
                    face = message.faces,
                    userData = userData
                )
            }

            Spacer(modifier = Modifier.height(24.dp))

            // Speech bubble
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.fillMaxWidth()
            ) {
                Image(
                    modifier = Modifier.width(16.dp),
                    painter = painterResource(Res.drawable.triangle),
                    contentDescription = stringResource(Res.string.triangle_desc),
                    colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
                )
                OutlinedCard(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp)
                ) {
                    Text(
                        modifier = Modifier.padding(16.dp).fillMaxWidth(),
                        text = message.text,
                        style = MaterialTheme.typography.bodyLarge,
                        textAlign = TextAlign.Center
                    )
                }
            }

            if (page == notificationsPage) {
                Spacer(modifier = Modifier.height(16.dp))
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    if (!notificationsGranted) {
                        Button(
                            onClick = onRequestNotifications,
                            modifier = Modifier
                                .fillMaxWidth(0.8f)
                                .semantics { testTag = "intro_btn_request_notifications" }
                        ) {
                            Text(stringResource(Res.string.intro_btn_request_notifications))
                        }
                    } else {
                        OutlinedButton(
                            onClick = {},
                            enabled = false,
                            modifier = Modifier
                                .fillMaxWidth(0.8f)
                                .semantics { testTag = "intro_btn_notifications_granted" }
                        ) {
                            Text(stringResource(Res.string.intro_notifications_granted))
                        }
                    }
                }
            }

            if (page == timerPage) {
                Spacer(modifier = Modifier.height(16.dp))
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    if (!exactAlarmGranted) {
                        Button(
                            onClick = onRequestExactAlarm,
                            modifier = Modifier
                                .fillMaxWidth(0.8f)
                                .semantics { testTag = "intro_btn_request_timer" }
                        ) {
                            Text(stringResource(Res.string.intro_btn_request_timer))
                        }
                    } else {
                        OutlinedButton(
                            onClick = {},
                            enabled = false,
                            modifier = Modifier
                                .fillMaxWidth(0.8f)
                                .semantics { testTag = "intro_btn_timer_granted" }
                        ) {
                            Text(stringResource(Res.string.intro_timer_granted))
                        }
                    }
                }
            }

            if (page == 0) {
                Spacer(modifier = Modifier.height(16.dp))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Icon(
                        imageVector = FeatherIcons.ChevronDown,
                        contentDescription = stringResource(Res.string.scroll_down_desc),
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(24.dp)
                    )
                    Spacer(Modifier.width(8.dp))
                    Text(
                        text = stringResource(Res.string.scroll_down_label),
                        style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold),
                        color = MaterialTheme.colorScheme.primary
                    )
                }
            }
        }
    }
}
